import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine, RiCheckLine } from '@remixicon/react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import MfaEnrollModal from './MfaEnrollModal';

const Switch = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-primary-700' : 'bg-gray-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-[22px]' : 'translate-x-0.5'
      }`}
    />
  </button>
);

const TwoFactorAuthModal = ({ open, onClose }) => {
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [initialEnabled, setInitialEnabled] = useState(false);
  const [verifiedFactorIds, setVerifiedFactorIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data, error } = await supabase.auth.mfa.listFactors();
        if (error) throw error;
        const verified = (data?.totp || []).filter((f) => f.status === 'verified');
        if (cancelled) return;
        const enabled = verified.length > 0;
        setVerifiedFactorIds(verified.map((f) => f.id));
        setOtpEnabled(enabled);
        setInitialEnabled(enabled);
      } catch (err) {
        if (!cancelled) toast.error(err.message || 'Failed to load MFA settings', { position: 'bottom-right' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  if (enrollOpen) {
    return (
      <MfaEnrollModal
        open
        onClose={() => {
          setEnrollOpen(false);
          setOtpEnabled(initialEnabled);
          setSaving(false);
        }}
        onSuccess={() => {
          setEnrollOpen(false);
          onClose();
        }}
      />
    );
  }

  const handleSave = async () => {
    if (saving) return;
    if (otpEnabled === initialEnabled) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      if (otpEnabled && !initialEnabled) {
        setEnrollOpen(true);
        return;
      }
      if (!otpEnabled && initialEnabled) {
        for (const factorId of verifiedFactorIds) {
          const { error } = await supabase.auth.mfa.unenroll({ factorId });
          if (error) throw error;
        }
        toast.success('Two-factor authentication disabled', { position: 'bottom-right' });
        onClose();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update MFA settings', { position: 'bottom-right' });
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-md bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h3 className="text-lg font-semibold text-primary-700">Two-factor authentication</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded-full"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pb-6">
          <div className="flex items-start gap-4 py-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">One-time Passcode</p>
              <p className="text-xs text-gray-500 mt-0.5">
                OTP will be sent to verified phone number
              </p>
            </div>
            <Switch
              checked={otpEnabled}
              onChange={setOtpEnabled}
              disabled={loading || saving}
            />
          </div>

          <button
            type="button"
            onClick={handleSave}
            disabled={loading || saving}
            className={`mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-colors ${
              loading || saving
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-primary-700 text-white hover:bg-primary-800'
            }`}
          >
            <RiCheckLine className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save preferences'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default TwoFactorAuthModal;
